import TextField from "@mui/material/TextField";
import React, {useRef, useState} from "react";
import Box from "@mui/material/Box";
import {TextFieldStyled} from "../form/FormField";
import MenuItem from "@mui/material/MenuItem";
import ro from "date-fns/locale/ro";
import DatePicker from "react-datepicker";
import {CustomInputRange} from "../form/CustomDatePicker";
import CustomButton from "../ui/CustomButton";
import Icon from "../ui/Icon";
import Typography from "@mui/material/Typography";
import {useTheme} from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import {TableColumnsWithFilter} from "./_interfaces";
import CustomSchoolYearPicker from "../form/CustomSchoolYearSelect";

interface FieldProps {
    column: TableColumnsWithFilter;
    value: any;
    onChange: (value: any) => void;
    filterOptions?: any;
}

interface FiltersRenderFieldProps {
    column: TableColumnsWithFilter;
    values: any;
    onChange: (filterType: string, columnId: string, value: any) => void;
    startDate?: Date | undefined;
    endDate?: Date | undefined;
    handleReset: (filterType: string, columnId: string) => void;
    filterOptions?: any;
}

const SelectFieldForColumn = ({column, value, onChange, filterOptions = {}}: FieldProps) => {
    const options = Array.isArray(filterOptions[column.id])
        ? filterOptions[column.id]
        : [];

    return (
        <Box sx={{border: "1px solid #ddd", borderRadius: "16px"}} width="100%">
            <TextFieldStyled
                name={`${column.id}_subject`}
                SelectProps={{
                    MenuProps: {
                        PaperProps: {
                            sx: {
                                backgroundColor: "primary.light",
                                borderRadius: "15px",
                            },
                        },
                        BackdropProps: {
                            invisible: true,
                        },
                    },
                }}
                fullWidth
                select
                margin="normal"
                label={typeof column.label === "string" ? column.label : "Alege materia"}
                value={value ?? ""}
                onChange={(e) => onChange(e.target.value)}
            >
                {options.map((option: any, index: number) =>
                    <MenuItem key={index} value={option.value}>
                        {option.label}
                    </MenuItem>
                )}
            </TextFieldStyled>
        </Box>
    );
};

const SchoolYearField = ({ value, onChange }: FieldProps) => {
    const selectedSchoolYear =
        Array.isArray(value) && value[0]
            ? new Date(value[0]).getFullYear()
            : "";

    return (
        <CustomSchoolYearPicker
            value={selectedSchoolYear}
            onChange={([startDate, endDate]) => {
                onChange([startDate, endDate]);
            }}
        />
    );
};

const DateRangeField = ({column, value, onChange}: FieldProps) => {
    const inputRef = useRef();

    const selectedStart = Array.isArray(value) && value[0] ? new Date(value[0]) : null;
    const selectedEnd = Array.isArray(value) && value[1] ? new Date(value[1]) : null;

    return (
        <Box sx={{border: "1px solid #ddd", borderRadius: "16px"}} width="100%">
            <DatePicker
                // @ts-ignore
                locale={ro}
                name={`${column.id}_dateRange`}
                selected={selectedStart}
                onChange={(dates: [Date | null, Date | null]) => {
                    onChange(dates)
                }}
                startDate={selectedStart}
                endDate={selectedEnd}
                selectsRange
                customInput={<CustomInputRange ref={inputRef} error={false}/>}
                dateFormat="dd/MM/yyyy"
                popperPlacement="bottom-start"
                popperModifiers={[
                    {
                        name: "flip",
                        options: {
                            fallbackPlacements: ["top"],
                        },
                    },
                ]}
                popperClassName="date-range-popper"
            />
        </Box>
    );
}

const NumberField =({column, value, onChange}: FieldProps) => {
    const [operator, setOperator] = useState<string>(Array.isArray(value) && value[0] ? value[0] : "=");
    const selectedValue = Array.isArray(value) && value[1] ? value[1] : undefined;
    const options = ["=", ">", ">=", "<", "<="];

    return (
        <>
            <Box mr={1} sx={{border: "1px solid #ddd", borderRadius: "16px"}} width="75px">
                <TextFieldStyled
                    name={`${column.id}_operator`}
                    SelectProps={{
                        MenuProps: {
                            PaperProps: {
                                sx: {
                                    backgroundColor: "primary.light",
                                    borderRadius: "15px",
                                },
                            },
                            BackdropProps: {
                                invisible: true,
                            },
                        },
                    }}
                    select
                    fullWidth
                    margin="normal"
                    label={""}
                    value={operator}
                    onChange={(e) => {
                        setOperator(e.target.value);
                        if(selectedValue === undefined) return;
                        onChange([e.target.value, selectedValue]);
                    }}
                >
                    {options.map((option, index) =>
                        <MenuItem key={index} value={option}>
                            {option}
                        </MenuItem>
                    )}
                </TextFieldStyled>
            </Box>
            <Box sx={{border: "1px solid #ddd", borderRadius: "16px"}} width="100%">
                <TextFieldStyled
                    fullWidth
                    margin="normal"
                    label={String(column.label)}
                    value={selectedValue}
                    onChange={(e) => onChange([operator, e.target.value])}
                    type="controlled-number"
                />
            </Box>
        </>
    )
}
const FiltersRenderField = ({column, values, onChange, handleReset, filterOptions = {}}: FiltersRenderFieldProps) => {
    const type = column.filterType ?? (column.numeric ? "number" : "text");
    const value = values[column.id] ?? "";
    const theme = useTheme();
    const isMdUp = useMediaQuery(theme.breakpoints.up("md"));

    const renderField = () => {
        switch (type) {
            case "number":
                return (
                    <NumberField
                        value={value}
                        column={column}
                        onChange={(val: any) => onChange(column.filterType ?? "", column.id, val)}
                    />
                );
            case "dateRange":
                return (
                    <DateRangeField
                        value={value}
                        column={column}
                        onChange={(val: any) => onChange(column.filterType ?? "", column.id, val)}
                    />
                );
            case "select":
                return (
                    <SelectFieldForColumn
                        filterOptions={filterOptions}
                        column={column}
                        value={value}
                        onChange={(val) => onChange(column.filterType ?? "", column.id, val)}
                    />
                );
            case "schoolYear":
                return (
                    <SchoolYearField
                        column={column}
                        value={value}
                        onChange={(val: any) => onChange(column.filterType ?? "", column.id, val)}
                    />
                );
            case "text":
            default:
                return (
                    <TextField
                        label={`Conține (${String(column.label)})`}
                        type="text"
                        size="small"
                        fullWidth
                        value={value}
                        onChange={(e) => onChange(column.filterType ?? "", column.id, e.target.value)}
                    />
                );
        }
    }

    return (
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
            <CustomButton
                bgcolor="white"
                buttonType="icon"
                onClick={() => handleReset(column.filterType ?? "", column.id)}
                appearance="circle"
                disabled={column.hasDefaultValue}
                width="32px"
                height="32px"
                icon={<Icon icon="fal xmark" fixedWidth={true} sx={{fontSize: "1.25rem"}}/>}
                sxExtra={{
                    color: "secondary.main",
                    backgroundColor: "transparent",
                    "&:hover": {
                        color: "secondary.light",
                        backgroundColor: "transparent",
                    },
                    "&[aria-expanded='true']": {
                        backgroundColor: "white",
                    },
                    "&.Mui-disabled": {
                        border: "none",
                    },
                }}
            />
            {isMdUp && <Typography variant="body2" mr={1} ml={1}>{column.label}:</Typography>}
            <>{renderField()}</>
        </Box>
    );
};

export default FiltersRenderField;